// Home Statistics (owner task 04): the pure part of the admin screen. A tile
// can follow a LIVE SOURCE, which the API computes on GET /home-stats/active
// (BE home-stats.sources.ts), so Instructors, Countries and Courses cannot
// drift from the platform. "Typed value" keeps the number staff enter.

export const HOME_STAT_SOURCES = ['none', 'instructors', 'countries', 'courses'] as const;
export type HomeStatSource = typeof HOME_STAT_SOURCES[number];
export type LiveSource = Exclude<HomeStatSource, 'none'>;

export const SOURCE_LABELS: Record<HomeStatSource, string> = {
  none: 'Typed value',
  instructors: 'Live: active instructors',
  countries: 'Live: countries of our students',
  courses: 'Live: published Kayfa courses',
};

export const SOURCE_HELP: Record<HomeStatSource, string> = {
  none: 'The site shows the value you type.',
  instructors: 'Counts the active instructor profiles (partner instructors excluded). Updates by itself.',
  countries: 'Counts the countries in our students’ profiles. Updates by itself.',
  courses: 'Counts the published Kayfa courses (partner courses excluded). Updates by itself.',
};

export type LiveCounts = Record<LiveSource, number | null> & { computedAt: string | null };

export const isLiveSource = (s: unknown): s is LiveSource =>
  s === 'instructors' || s === 'countries' || s === 'courses';

/** A row's source; an older row without one (or an unknown value) is 'none'. */
export const sourceOf = (row: { source?: unknown } | null | undefined): HomeStatSource =>
  isLiveSource(row?.source) ? (row!.source as LiveSource) : 'none';

const num = (v: unknown): number | null =>
  typeof v === 'number' && Number.isFinite(v) && v >= 0 ? v : null;

/** GET /home-stats/live-counts → the counts; anything malformed is null (the screen then says "not available"). */
export function liveCountsFrom(data: unknown): LiveCounts | null {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
  const d = data as Record<string, unknown>;
  return {
    instructors: num(d.instructors),
    countries: num(d.countries),
    courses: num(d.courses),
    computedAt: typeof d.computedAt === 'string' ? d.computedAt : null,
  };
}

/**
 * The number the site shows (website homeStatsView.ts statValue, the same rule):
 * with "+" it is rounded DOWN to two significant figures from 100 up
 * (15,237 → 15,000; 412 → 410; 85 stays), without "+" the value as it is.
 */
export function shownValue(value: unknown, plus: boolean): number {
  const v = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(v) || v <= 0) return 0;
  if (!plus) return Math.round(v);
  const whole = Math.floor(v);
  if (whole < 100) return whole;
  const step = 10 ** (String(whole).length - 2);
  return Math.floor(whole / step) * step;
}

type Row = { value?: unknown; suffixPlus?: boolean; source?: unknown };

/** The value the site uses for this row: the live count when it has one, else the typed value. */
export function effectiveValue(row: Row, counts: LiveCounts | null): { value: number; live: boolean } {
  const source = sourceOf(row);
  const live = source !== 'none' && counts ? counts[source] : null;
  return live !== null && live !== undefined
    ? { value: live, live: true }
    : { value: Number(row.value) || 0, live: false };
}

/** "15,000+" — exactly how the public home page renders this stat. */
export function previewText(row: Row, counts: LiveCounts | null): string {
  const plus = row.suffixPlus !== false;
  const { value } = effectiveValue(row, counts);
  return `${shownValue(value, plus).toLocaleString('en-US')}${plus ? '+' : ''}`;
}

/** The hint under the source picker: what the live count is right now, or why the typed value is used. */
export function liveHint(source: HomeStatSource, counts: LiveCounts | null): string {
  if (source === 'none') return SOURCE_HELP.none;
  const n = counts ? counts[source] : null;
  const now =
    n === null || n === undefined
      ? 'The live number is not available right now, so the site shows the typed value below.'
      : `Right now: ${n.toLocaleString('en-US')}.`;
  return `${SOURCE_HELP[source]} ${now}`;
}
